import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    TaskTypeBreadcrumbs,
    TaskTypeCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TaskTypeActions from "../../../actions/taskTypes";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { QALookupsLayout } from "../../index";

import "./TaskTypePage.scss";

interface TaskTypePageProps {
    form: Dtos.TaskType & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    taskType: Dtos.TaskType;
    lookups: Dtos.Lookup[];
    loadingTaskType: boolean;
    loadTaskTypeFailure: boolean;
    loadTaskTypeErrorCode: string;
    savingTaskType: boolean;
    saveTaskTypeSuccess: boolean;
    saveTaskTypeFailure: boolean;
    creatingTaskType: boolean;
    createTaskTypeSuccess: boolean;
    validatingTaskType: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTaskType: TaskTypeActions.ITaskTypeClearActionCreator;
    loadTaskType: TaskTypeActions.ITaskTypeLoadByIdActionCreator;
    saveTaskType: TaskTypeActions.ITaskTypeSaveActionCreator;
    createTaskType: TaskTypeActions.ITaskTypeCreateActionCreator;
    validateTaskType: TaskTypeActions.ITaskTypeValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.taskType";

const TaskTypePage: FC<TaskTypePageProps> = ({
    form,
    formState,
    formProperties,
    taskType,
    lookups,
    loadingTaskType,
    loadTaskTypeFailure,
    loadTaskTypeErrorCode,
    savingTaskType,
    saveTaskTypeSuccess,
    saveTaskTypeFailure,
    creatingTaskType,
    createTaskTypeSuccess,
    validatingTaskType,
    validationFailures,
    permissions,
    clearTaskType,
    loadTaskType,
    saveTaskType,
    createTaskType,
    validateTaskType,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { taskTypeId } = useParams<{ taskTypeId: string }>();
    const taskTypeIdNumber = parseInt(taskTypeId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.TaskType = {} as Dtos.TaskType;

        loadForm(reduxFormName, Object.assign({ ...taskType }, extraData));
    };


    // Load task type
    useEffect(() => {
        if (taskTypeIdNumber && !taskType && !loadingTaskType) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(TaskTypeBreadcrumbs(taskTypeIdNumber));
            loadTaskType(taskTypeIdNumber);
        } else if (
            !taskTypeIdNumber &&
            !creatingTaskType &&
            !createTaskTypeSuccess
        ) {
            createTaskType();
            setBreadcrumbs(TaskTypeCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (taskType && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        taskTypeIdNumber,
        taskType,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        TaskTypeBreadcrumbs,
        loadTaskType,
        createTaskType,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingTaskType,
        creatingTaskType,
        createTaskTypeSuccess,
        TaskTypeCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTaskTypeFailure) {
            navigate(`/error/${loadTaskTypeErrorCode}`);
        }
    }, [loadTaskTypeFailure, loadTaskTypeErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (taskTypeIdNumber && taskType?.value) {
            setBreadcrumbs(
                TaskTypeBreadcrumbs(taskTypeIdNumber, taskType?.value)
            );
        }
    }, [taskTypeIdNumber, taskType, setBreadcrumbs, TaskTypeBreadcrumbs]);

    // Reload task type after saving
    useEffect(() => {
        if (!savingTaskType && saveTaskTypeSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Task type saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/qa/tasktypes";
                navigate(url);
            } else if (!taskTypeIdNumber) {
                let url: string = `/settings/qa/tasktypes/${taskType?.id}`;
                navigate(url);
                clearTaskType();
            }
        }
    }, [
        savingTaskType,
        saveTaskTypeSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTaskType && saveTaskTypeFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTaskType, saveTaskTypeFailure]);

    // Cleanup effect to clear the task types on component unmount
    useEffect(() => {
        return () => {
            clearTaskType();
            resetForm(reduxFormName);
        };
    }, [clearTaskType, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/qa/tasktypes";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.TaskType) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TaskType = {} as Dtos.TaskType;

            await saveTaskType(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TaskType = {} as Dtos.TaskType;

            if (form && hasBeenSubmitted && !validatingTaskType) {
                await validateTaskType(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.TaskTypesUpdate,
                Dtos.Permission.TaskTypesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTaskType ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "taskType",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "taskType",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "taskType",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "taskType",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingTaskType,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <QALookupsLayout
            loading={loadingTaskType}
            permission={Dtos.Permission.TaskTypesView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TaskTypePageProps> => {
    let taskType: Dtos.TaskType | undefined = !(
        state.taskTypes.formData instanceof Array
    )
        ? state.taskTypes.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        taskType: taskType!,
        loadingTaskType:
            state.taskTypes.loadState &&
            state.taskTypes.loadState.status == RequestState.Pending,
        loadTaskTypeFailure:
            state.taskTypes.loadState &&
            state.taskTypes.loadState.status == RequestState.Failure,
        loadTaskTypeErrorCode: state.taskTypes.loadState.errorCode,
        savingTaskType:
            state.taskTypes.saveState &&
            state.taskTypes.saveState.status == RequestState.Pending,
        saveTaskTypeSuccess:
            state.taskTypes.saveState &&
            state.taskTypes.saveState.status == RequestState.Success,
        saveTaskTypeFailure:
            state.taskTypes.saveState &&
            state.taskTypes.saveState.status == RequestState.Failure,
        creatingTaskType:
            state.taskTypes.createState &&
            state.taskTypes.createState.status == RequestState.Pending,
        createTaskTypeSuccess:
            state.taskTypes.createState &&
            state.taskTypes.createState.status == RequestState.Success,
        validatingTaskType:
            state.taskTypes.validateState &&
            state.taskTypes.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.taskType,
        formState: state.taskTypes.formState,
        formProperties: state.taskTypes.formProperties,
        validationFailures: taskType
            ? state.taskTypes.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<TaskTypePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTaskType: bindActionCreators(
            TaskTypeActions.LoadTaskTypeById,
            dispatch
        ),
        clearTaskType: bindActionCreators(TaskTypeActions.Clear, dispatch),
        saveTaskType: bindActionCreators(
            TaskTypeActions.SaveTaskType,
            dispatch
        ),
        createTaskType: bindActionCreators(
            TaskTypeActions.CreateTaskType,
            dispatch
        ),
        validateTaskType: bindActionCreators(
            TaskTypeActions.ValidateTaskType,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TaskTypePage);
